<template>
  <div class="flow-container">
    <div class="path-list-container">
      <div class="sub-heading sub-heading-left">
        <h5 class="title is-6">
          Paths
          <n-tooltip
            position="right"
            text="Paths are used to create sections of a flow. A path ends when there are no more states to process. The flow can continue by transitioning to other paths"
          >
            <a @click="addNewPath()" class="add-button">
              <AddIcon title="" class="icon-1-3x"></AddIcon>
            </a>
          </n-tooltip>
        </h5>
      </div>
      <aside class="menu">
        <ul class="menu-list">
          <li
            class="first-path path-list-item"
            :class="{ 'is-active': isPathActive(firstPath.id) }"
            @click="setCurrentPath(firstPath.id)"
          >
            <div class="path-item">
              <n-tooltip position="top" text="The first path cannot be moved or deleted" />
              <a class="path-link">{{ firstPath.name }}</a>
              <span class="invalid-warning" v-if="isPathInvalid(firstPath.id)">
                <ErrorIcon class="icon-1-3x"></ErrorIcon>
              </span>
            </div>
          </li>
          <Draggable v-model="reorderablePathList" item-key="id">
            <template #item="{ element: path }">
              <li class="path-list-item" :key="path.id" @click="setCurrentPath(path.id)">
                <div class="draggable path-item" :class="{ 'is-active': isPathActive(path.id) }">
                  <span class="icon draggable-icon">
                    <DragIcon color="#A9A9A9" class="icon-1-5x"></DragIcon>
                  </span>
                  <a class="path-link">{{ path.name }}</a>
                  <span class="invalid-warning" v-if="isPathInvalid(path.id)">
                    <ErrorIcon class="icon-1-3x"></ErrorIcon>
                  </span>
                </div>
              </li>
            </template>
          </Draggable>
        </ul>
      </aside>
    </div>
    <PathDesigner></PathDesigner>
  </div>
</template>

<script lang="ts" setup>
import Draggable from 'vuedraggable';
import { nanoid } from 'nanoid';
import { PathViewModel } from '@/features/bots/models/designer';
import PathDesigner from '../components/designer/PathDesigner.vue';
import { SET_PATHS, ADD_PATH, SET_CURRENT_PATH } from '@/store/bot-designer/paths/mutationTypes';
import DragIcon from '@/shared/components/DragIcon.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import AddIcon from 'vue-material-design-icons/PlusCircle.vue';
import HelpIcon from 'vue-material-design-icons/HelpCircleOutline.vue';
import { LOAD_HOLIDAYS } from '@/store/holidays/actionTypes';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import { useStore } from 'vuex';
import { computed, onBeforeMount } from 'vue';

const store = useStore();
const paths = computed(() => store.getters.paths);
const currentPathId = computed(() => store.getters.currentPathId);
const firstPath = computed(() => store.getters.firstPath);
const isPathInvalid = computed(() => store.getters.isPathInvalid);

const setPaths = (paths: PathViewModel[]) => store.commit(SET_PATHS, paths);
const addPath = (path: PathViewModel) => store.commit(ADD_PATH, path);
const setCurrentPath = (id: string) => store.commit(SET_CURRENT_PATH, id);

const loadHolidays = (forceReload = false): Promise<HolidayViewModel[]> => store.dispatch(LOAD_HOLIDAYS, forceReload);

onBeforeMount(() => loadHolidays());

const reorderablePathList = computed({
  get() {
    return paths.value.slice(1);
  },
  set(value) {
    value.unshift(firstPath.value);
    setPaths(value);
  },
});

function addNewPath() {
  const newPath = {
    id: `path-${nanoid(7)}`,
    name: `New Path`,
    states: [],
  } as PathViewModel;

  addPath(newPath);
  setCurrentPath(newPath.id);
}

function isPathActive(pathId: string): boolean {
  return currentPathId.value === pathId;
}
</script>

<style scoped lang="scss">
.path-list-container {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  flex: 1;
  overflow: hidden;
}

.flow-container {
  display: flex;
  flex: 1;

  aside.menu {
    padding: 1rem;

    .menu-list a.is-active {
      background-color: #f5e799;
      color: initial;
    }
  }
}

.path-item {
  display: flex;
  align-items: center;
}

.path-link {
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

h5 {
  line-height: 1em;
  padding-left: 1rem;
  line-height: 1;
}

a.add-button {
  span {
    height: 1.25rem;
    font-size: 1rem;
    vertical-align: bottom;
  }
}

.is-active {
  background-color: #f5e799;
  color: initial;
}

.draggable {
  cursor: pointer;
  a {
    display: inline-block;
  }

  .draggable-icon {
    color: #a9a9a9;
    cursor: move;
    visibility: hidden;
    margin-left: 7px;
  }
}

.draggable:hover {
  background-color: #f5f5f5;

  &.is-active {
    background-color: #f5e799;
    color: initial;
  }

  .draggable-icon {
    visibility: visible;
  }
}

:deep(.first-path) {
  cursor: pointer;
  .n-tooltip {
    .icon {
      margin-left: 8px;
      .n-tooltip__icon {
        position: unset;
        bottom: unset;
      }
    }
  }
}

.first-path:hover {
  background-color: #f5f5f5;

  &.is-active {
    background-color: #f5e799;
    color: initial;
  }
}

.invalid-warning {
  color: red;
  margin-right: 0.5em;
}
</style>
